// 🦠 B247 (AI_111) — NORMALIZADOR DE SOROLOGIAS / AUTOANTICORPOS (puro, testável)
// ─────────────────────────────────────────────────────────────────────────────
// FONTE ÚNICA da padronização (decisão de Leonardo). Converte qualquer entrada
// (Positivo, Reagente, "+", número/índice, Detectável, "1:80"…) no formato padrão.
// Será consumido por: entrada manual, import por PDF, IA (prompts/anonimização) e
// pesquisa — assim a regra mora num lugar só, é testável e não diverge.
//
// REGRAS (Leonardo, AI_111):
//  • Qualitativas (HBsAg, anti-HCV, anti-HBc, HIV, IgG/IgM…) → Reagente / Não
//    Reagente / Indeterminado.
//  • anti-HBs → também qualitativa, com corte ≥10 mUI/mL = Reagente (imune).
//  • Carga viral / PCR → Detectável / Indetectável (valor numérico preservado à parte).
//  • Genotipagem → texto do genótipo (sem padronizar).
//  • Autoanticorpos: FAN/ASMA/anti-LKM1/AMA → título (1/80…); anti-transglutaminase
//    e anti-DNA → numérico; ANCA → padrão (c/p-ANCA).
// Fase 1 = só esta lógica + testes (sem mexer em UI/IA). null = não foi possível
// padronizar (vazio ou texto não reconhecido) — o chamador decide o que fazer.

// ── Opções padronizadas (reusadas por UI e catálogo de pesquisa) ──────────────
export const OPCOES_QUALITATIVA = ["Reagente", "Não Reagente", "Indeterminado"] as const;
export const OPCOES_ANCA = ["Negativo", "c-ANCA", "p-ANCA", "ANCA atípico"] as const;

// Carga viral é NUMÉRICA; estes rótulos são para quando não há valor (nunca 0).
export const ROTULO_CARGA_NAO_DETECTADO = "Não detectado";
export const ROTULO_CARGA_NAO_QUANTIFICAVEL = "Detectável (não quantificável)";
export const ROTULOS_CARGA_VIRAL = [
  ROTULO_CARGA_NAO_DETECTADO,
  ROTULO_CARGA_NAO_QUANTIFICAVEL,
] as const;
export const TITULOS_AUTOANTICORPO = [
  "Negativo",
  "1/40",
  "1/80",
  "1/160",
  "1/320",
  "1/640",
  "1/1280",
  "1/2560",
] as const;

export type ResultadoQualitativo = "Reagente" | "Não Reagente" | "Indeterminado";
export type RotuloCargaViral = "Não detectado" | "Detectável (não quantificável)";

// Corte do anti-HBs (imunidade) em mUI/mL.
export const CORTE_ANTI_HBS = 10;
// Corte genérico de índice (S/CO) das sorologias qualitativas reportadas por número.
export const CORTE_INDICE_QUALITATIVO = 1;

// ⚠️ B336 (AI_149) — ENSAIOS COMPETITIVOS: a direção do índice é INVERTIDA.
// O anti-HBc TOTAL por eletroquimioluminescência (e por vários outros métodos) é
// um imunoensaio COMPETITIVO: quanto MAIS anticorpo na amostra, MENOR o sinal.
// O laudo imprime, para esse exame, "Índice Cutoff: <1,0 - REAGENTE / >1,0 - NÃO
// REAGENTE" — o contrário de todos os vizinhos.
//
// Caso real (28/07/2026, Hospital de Base/RO): anti-HBc TOTAL = 0,01 com essa
// faixa impressa → REAGENTE. O sistema classificou "Não Reagente" (aplicou o
// corte padrão) e a IA repetiu o erro. No MESMO laudo o anti-HBc IgM = 0,08 usa
// a direção NORMAL e é mesmo negativo — dois "0,0x" vizinhos com sentidos opostos.
//
// Como NÃO dá para saber a plataforma a partir de um número solto, a regra é
// NÃO CHUTAR: número puro nestes campos não vira polaridade (o chamador mostra o
// valor cru para conferência). Só há classificação quando o texto traz a palavra
// (Reagente/Não Reagente) ou quando a direção do corte vem informada.
// `antiHBe` entrou junto (decisão de Leonardo, 28/07): também é competitivo em
// várias plataformas (índice < 1,0 = reagente). Efeito nulo quando o laudo traz a
// palavra escrita — que é como a maioria reporta —, e fecha o mesmo caminho de
// erro silencioso quando vier como índice.
export const SOROLOGIAS_INDICE_COMPETITIVO = new Set(["antiHBcIgG", "antiHBe"]);

/** Direção do corte quando ela é conhecida (vinda da faixa impressa no laudo). */
export type DirecaoCorte = "padrao" | "competitivo";

// ── Helpers de texto/número ───────────────────────────────────────────────────
function norm(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

function vazio(v: unknown): boolean {
  return v === null || v === undefined || String(v).trim() === "";
}

// Operador de comparação em qualquer posição ("<10", ">1000", "detectável <12").
function operadorDe(t: string): "<" | ">" | "=" {
  const m = t.match(/(<=|>=|<|>|≤|≥)/);
  if (!m) return "=";
  const o = m[1];
  if (o === "≤" || o === "<=") return "<";
  if (o === "≥" || o === ">=") return ">";
  return o as "<" | ">";
}

// Extrai um número de formatos heterogêneos (vírgula/ponto decimal, separador de
// milhar, notação científica "1,2x10^6", unidades). null se não houver número.
export function parseNumero(raw: unknown): number | null {
  const t = norm(raw).replace(/[<>=≤≥]/g, "").trim();
  if (!t) return null;

  const sci = t.match(/([\d.,]+)\s*[x×]\s*10\s*\^?\s*(\d+)/);
  if (sci) {
    const base = paraFloatBR(sci[1]);
    const exp = parseInt(sci[2], 10);
    return base === null ? null : base * Math.pow(10, exp);
  }
  return paraFloatBR(t);
}

function paraFloatBR(s: string): number | null {
  let x = s.replace(/[^\d.,]/g, "");
  if (!x) return null;
  if (x.includes(".") && x.includes(",")) {
    x = x.replace(/\./g, "").replace(",", "."); // 1.234,5 → 1234.5
  } else if (x.includes(",")) {
    x = x.replace(",", "."); // 1,2 → 1.2
  } else if ((x.match(/\./g) || []).length > 1) {
    x = x.replace(/\./g, ""); // 1.234.567 → 1234567
  } else if (/^\d{1,3}\.\d{3}$/.test(x)) {
    x = x.replace(".", ""); // ponto + 3 dígitos = milhar (350.000 → 350000)
  } // demais casos de ponto único = decimal (10.5, 1.05)
  const n = parseFloat(x);
  return Number.isFinite(n) ? n : null;
}

function classePorCorte(
  operador: "<" | ">" | "=",
  valor: number,
  corte: number
): ResultadoQualitativo {
  if (operador === "<") return valor <= corte ? "Não Reagente" : "Indeterminado";
  if (operador === ">") return valor >= corte ? "Reagente" : "Indeterminado";
  return valor >= corte ? "Reagente" : "Não Reagente";
}

// Classifica SÓ pelo texto (sem número). null se não reconhecer.
function classificarTextoQual(t: string): ResultadoQualitativo | null {
  if (t === "+") return "Reagente";
  if (t === "-" || t === "nr") return "Não Reagente";
  if (/indetermin|inconclus|duvidos|limitrof|borderline|equivoc|zona cinz/.test(t))
    return "Indeterminado";
  if (/\bneg\b|negativ|nao reagente|nao reativ|ausente|nao detect|indetect|nao reage/.test(t))
    return "Não Reagente";
  if (/\breag\b|reagente|reativ|positiv|\bpos\b|detect|presente|reage/.test(t))
    return "Reagente";
  return null;
}

// ── Normalizadores por tipo ───────────────────────────────────────────────────

// Qualitativa (corte de índice configurável; anti-HBs usa corte 10).
// `direcao` (B336): "competitivo" inverte a leitura do índice (índice BAIXO =
// Reagente). Só informe quando a direção for CONHECIDA — tipicamente lida da
// faixa de referência impressa no laudo.
export function normalizarQualitativa(
  raw: unknown,
  corte: number = CORTE_INDICE_QUALITATIVO,
  direcao: DirecaoCorte = "padrao"
): ResultadoQualitativo | null {
  if (vazio(raw)) return null;
  const t = norm(raw);
  const txt = classificarTextoQual(t);
  if (txt) return txt; // a palavra do laudo sempre manda
  const valor = parseNumero(t);
  if (valor === null) return null; // texto não reconhecido → chamador decide
  if (direcao === "competitivo") {
    // Índice competitivo: abaixo do corte = REAGENTE (inverso do usual).
    if (operadorDe(t) === "<") return "Reagente";
    if (operadorDe(t) === ">") return "Não Reagente";
    return valor < corte ? "Reagente" : "Não Reagente";
  }
  return classePorCorte(operadorDe(t), valor, corte);
}

export function normalizarAntiHBs(raw: unknown): ResultadoQualitativo | null {
  return normalizarQualitativa(raw, CORTE_ANTI_HBS);
}

// Carga viral (HBV-DNA, HCV-RNA, HIV, CMV, EBV, Delta): NUMÉRICA — a conduta
// depende do valor absoluto e da variação (e do log). Quando NÃO há número,
// devolve um RÓTULO (NUNCA 0, que distorceria média/log e quebraria escala log):
//   "Não detectado"  = indetectável / abaixo do limite de DETECÇÃO
//   "Detectável (não quantificável)" = vê o vírus, mas abaixo do limite de MEDIDA
// Decisão de Leonardo (AI_111). Retorna number | rótulo | null.
export function normalizarCargaViral(
  raw: unknown
): number | RotuloCargaViral | null {
  if (vazio(raw)) return null;
  const t = norm(raw);
  if (/nao quantific|nao mensurav|abaixo.*quantif|<\s*l[oq]q/.test(t))
    return ROTULO_CARGA_NAO_QUANTIFICAVEL;
  if (/indetect|nao detect|nao reagente|\bneg\b|negativ|ausente/.test(t))
    return ROTULO_CARGA_NAO_DETECTADO;
  const valor = parseNumero(t);
  if (valor !== null) {
    if (operadorDe(t) === "<")
      return /detect/.test(t)
        ? ROTULO_CARGA_NAO_QUANTIFICAVEL // "detectável <15" = abaixo do LQ
        : ROTULO_CARGA_NAO_DETECTADO; // "<15" puro = abaixo do LD
    return valor;
  }
  if (/detect|positiv|reagente|reativ/.test(t))
    return ROTULO_CARGA_NAO_QUANTIFICAVEL; // detectável sem valor
  return null;
}

// Título de autoanticorpo (FAN/ASMA/anti-LKM1/AMA) → "1/80" | "Negativo" |
// "Reagente" (sem título) | "Indeterminado".
export function normalizarTitulo(raw: unknown): string | null {
  if (vazio(raw)) return null;
  const t = norm(raw);
  if (/\bneg\b|negativ|nao reagente|nao reativ|ausente|^nr$/.test(t)) return "Negativo";
  if (/indetermin|inconclus|duvidos/.test(t)) return "Indeterminado";
  const titulo = t.match(/1\s*[/:]\s*(\d+)/);
  if (titulo) return `1/${titulo[1]}`;
  const soloNum = t.match(/^(\d{1,5})$/);
  if (soloNum) return `1/${soloNum[1]}`;
  if (/reagente|reativ|positiv/.test(t)) return "Reagente";
  return null;
}

// Padrão de ANCA.
export function normalizarPadraoANCA(raw: unknown): string | null {
  if (vazio(raw)) return null;
  const t = norm(raw);
  if (/\bneg\b|negativ|nao reagente|ausente|^nr$/.test(t)) return "Negativo";
  if (/c-?anca|citoplasm/.test(t)) return "c-ANCA";
  if (/p-?anca|perinucle/.test(t)) return "p-ANCA";
  if (/atipic|atypical/.test(t)) return "ANCA atípico";
  // "positivo" sem padrão informado: NÃO chuta o padrão — deixa em branco para o
  // médico escolher c/p-ANCA na conferência (decisão de Leonardo, AI_111).
  return null;
}

// Numérico (anti-transglutaminase, anti-DNA) → número (sem unidade).
export function normalizarNumerico(raw: unknown): number | null {
  if (vazio(raw)) return null;
  return parseNumero(raw);
}

// ── Catálogo de tipos por campo (roteamento) ──────────────────────────────────
export type TipoSorologia = "qualitativa" | "antiHBs" | "cargaViral" | "genotipo";

export const TIPO_SOROLOGIA: Record<string, TipoSorologia> = {
  hbsag: "qualitativa",
  antiHBcIgG: "qualitativa",
  antiHBcIgM: "qualitativa",
  hbeag: "qualitativa",
  antiHBe: "qualitativa",
  antiHCV: "qualitativa",
  antiHAVIgG: "qualitativa",
  antiHAVIgM: "qualitativa",
  antiHDV: "qualitativa",
  antiHEV: "qualitativa",
  cmvIgG: "qualitativa",
  cmvIgM: "qualitativa",
  ebvIgG: "qualitativa",
  ebvIgM: "qualitativa",
  rubeolaIgG: "qualitativa",
  rubeolaIgM: "qualitativa",
  antiHIV: "qualitativa",
  vdrl: "qualitativa",
  ftaAbs: "qualitativa",
  toxoIgG: "qualitativa",
  toxoIgM: "qualitativa",
  chagas: "qualitativa",
  chagasIgG: "qualitativa",
  chagasIgM: "qualitativa",
  htlv: "qualitativa",
  // Rastreio pré-Tx — parasitas / TB (AI_118). PPD fica de fora (texto em mm).
  estrongiloidesIgG: "qualitativa",
  igraTB: "qualitativa",
  antiHBs: "antiHBs",
  hcvRNA: "cargaViral", // HCV-RNA
  pcrVHB: "cargaViral", // HBV-DNA
  pcrVHD: "cargaViral", // Delta
  cmvPCR: "cargaViral",
  ebvPCR: "cargaViral",
  genotipagemVHB: "genotipo",
};

export type TipoAutoanticorpo = "titulo" | "padrao" | "numerico";

export const TIPO_AUTOANTICORPO: Record<string, TipoAutoanticorpo> = {
  fan: "titulo",
  antiMusculoLiso: "titulo",
  antiLKM: "titulo",
  antiMitocondria: "titulo",
  anca: "padrao",
  antiDNA: "numerico",
  antiTransglutaminase: "numerico",
};

// Roteador: normaliza UM campo de sorologia pelo seu tipo. Campo desconhecido →
// devolve o texto cru aparado (passthrough, não perde dado).
export function normalizarSorologia(campo: string, raw: unknown): string | null {
  const tipo = TIPO_SOROLOGIA[campo];
  if (!tipo) return vazio(raw) ? null : String(raw).trim();
  if (tipo === "qualitativa") {
    // ⚠️ B336 — campo de ensaio COMPETITIVO (anti-HBc total): índice numérico
    // solto NÃO vira polaridade. Sem saber a direção do corte, classificar seria
    // chutar — e o chute errado esconde uma hepatite B (caso real de 28/07). A
    // palavra do laudo ("Reagente"/"Não Reagente") continua valendo normalmente.
    if (SOROLOGIAS_INDICE_COMPETITIVO.has(campo)) {
      const t = norm(raw);
      const soTexto = classificarTextoQual(t);
      if (soTexto) return soTexto;
      // Número solto: PRESERVA o índice cru (não perde o dado e não afirma uma
      // polaridade que não dá para saber). O médico vê "0,01" e confere; a IA
      // recebe o índice com a regra de interpretação (não o rótulo errado).
      return vazio(raw) ? null : String(raw).trim();
    }
    return normalizarQualitativa(raw);
  }
  if (tipo === "antiHBs") return normalizarAntiHBs(raw);
  if (tipo === "cargaViral") {
    const r = normalizarCargaViral(raw);
    return r === null ? null : String(r); // número como string OU rótulo
  }
  // genótipo: preserva o texto
  return vazio(raw) ? null : String(raw).trim();
}

// Roteador: normaliza UM autoanticorpo pelo seu tipo.
export function normalizarAutoanticorpo(campo: string, raw: unknown): string | null {
  const tipo = TIPO_AUTOANTICORPO[campo];
  if (!tipo) return vazio(raw) ? null : String(raw).trim();
  if (tipo === "titulo") return normalizarTitulo(raw);
  if (tipo === "padrao") return normalizarPadraoANCA(raw);
  const n = normalizarNumerico(raw); // numerico
  return n === null ? null : String(n);
}

// Padroniza TODOS os campos de sorologia presentes num registro, no momento de
// GRAVAR. Mantém os demais campos (dataColeta, pacienteId, auditoria…) intactos.
// Campo de sorologia vazio/não reconhecido vira undefined (não grava lixo).
export function padronizarRegistroSorologia<T extends Record<string, any>>(
  registro: T,
  opcoes: { manterOriginal?: boolean } = {}
): T {
  const out: Record<string, any> = { ...registro };
  for (const campo of Object.keys(TIPO_SOROLOGIA)) {
    if (Object.prototype.hasOwnProperty.call(out, campo)) {
      const bruto = out[campo];
      const v = normalizarSorologia(campo, bruto);
      // B338-FU (AI_150) — `null` do normalizador tem DUAS causas, e confundi-las
      // custa dado: (a) o médico LIMPOU o campo e (b) o texto não foi reconhecido.
      // Antes as duas viravam `undefined`, que os contextos filtravam → apagar um
      // campo não apagava nada (o valor antigo ficava, sem aviso). Agora:
      //   limpou       → "" (grava vazio, limpa de verdade — igual aos tumorais)
      //   não reconheceu → undefined (não grava lixo; a tela avisa, ver
      //                     camposNaoReconhecidos)
      // manterOriginal (uso na IA) segue preservando o valor bruto: a IA nunca
      // perde dado por não reconhecer formato.
      if (v !== null) out[campo] = v;
      // ⚠️ manterOriginal ANTES da regra do vazio: no caminho da IA
      // (anonimizarDados) o objeto é montado por LITERAL com todos os campos, e
      // exame NÃO FEITO chega como `undefined`. Se o vazio virasse "" aqui, o
      // JSON.stringify passaria a serializar dezenas de `"antiHIV": ""` — a IA
      // leria "resultado em branco" onde na verdade o exame não foi pedido, e o
      // payload inchava (medido: 2 → 9 chaves numa coleta com 2 resultados).
      // A regra "limpou → ''" só faz sentido ao GRAVAR o que veio da tela.
      else if (opcoes.manterOriginal) out[campo] = bruto;
      else out[campo] = vazio(bruto) ? "" : undefined;
    }
  }
  return out as T;
}

// Idem para MARCADORES: padroniza só os AUTOANTICORPOS presentes (título/padrão/
// numérico). Marcadores tumorais e demais campos numéricos ficam intactos.
export function padronizarRegistroMarcadores<T extends Record<string, any>>(
  registro: T,
  opcoes: { manterOriginal?: boolean } = {}
): T {
  const out: Record<string, any> = { ...registro };
  for (const campo of Object.keys(TIPO_AUTOANTICORPO)) {
    if (Object.prototype.hasOwnProperty.call(out, campo)) {
      const bruto = out[campo];
      const v = normalizarAutoanticorpo(campo, bruto);
      // B338-FU (AI_150) — mesma distinção do padronizarRegistroSorologia acima.
      if (v !== null) out[campo] = v;
      // ⚠️ manterOriginal ANTES da regra do vazio: no caminho da IA
      // (anonimizarDados) o objeto é montado por LITERAL com todos os campos, e
      // exame NÃO FEITO chega como `undefined`. Se o vazio virasse "" aqui, o
      // JSON.stringify passaria a serializar dezenas de `"antiHIV": ""` — a IA
      // leria "resultado em branco" onde na verdade o exame não foi pedido, e o
      // payload inchava (medido: 2 → 9 chaves numa coleta com 2 resultados).
      // A regra "limpou → ''" só faz sentido ao GRAVAR o que veio da tela.
      else if (opcoes.manterOriginal) out[campo] = bruto;
      else out[campo] = vazio(bruto) ? "" : undefined;
    }
  }
  return out as T;
}

/**
 * B338-FU (AI_150) — quais campos o médico preencheu com texto que o sistema
 * NÃO conseguiu interpretar?
 *
 * Existe para fechar uma falha silenciosa: ao gravar, campo não reconhecido vira
 * `undefined` e é descartado — o texto digitado some e o valor antigo permanece,
 * sem nenhum aviso. Campo VAZIO não entra aqui (limpar é intenção legítima, e
 * agora grava ""). A tela usa esta lista para perguntar antes de salvar.
 *
 * @returns rótulos dos campos não reconhecidos (chave crua se não houver rótulo).
 */
export function camposNaoReconhecidos(
  registro: Record<string, any>,
  rotulos: Record<string, string> = {},
  // B338-FU (AI_150) — na EDIÇÃO o registro vem inteiro do Firestore, então um
  // valor legado ilegível (import de PDF grava o bruto com manterOriginal, e
  // registros pré-B247 nunca passaram por normalização) faria o aviso disparar
  // mesmo quando o médico só mexeu na data — apontando um campo que a tela
  // renderiza VAZIO, porque o select normaliza para exibir. Passando o registro
  // ORIGINAL, só avisamos sobre o que ele realmente digitou agora.
  original?: Record<string, any>
): string[] {
  const fora: string[] = [];
  const checar = (
    campos: string[],
    fn: (campo: string, raw: unknown) => string | null
  ) => {
    for (const campo of campos) {
      if (!Object.prototype.hasOwnProperty.call(registro, campo)) continue;
      const bruto = registro[campo];
      if (vazio(bruto)) continue; // limpou → intenção legítima, não é erro
      // Campo intocado desde que a edição começou: problema herdado, não deste
      // salvamento — não é hora de cobrar do médico.
      if (original && Object.is(original[campo], bruto)) continue;
      if (fn(campo, bruto) === null) fora.push(rotulos[campo] || campo);
    }
  };
  checar(Object.keys(TIPO_SOROLOGIA), normalizarSorologia);
  checar(Object.keys(TIPO_AUTOANTICORPO), normalizarAutoanticorpo);
  return fora;
}
